import { compositionPageDimensionsMm, FILL_PAGE_BOUNDS } from './geometry';
import { createDefaultInsetMap, type InsetDefaultContent } from './insetDefaults';
import type { CompositionDraft, CompositionTypography, NormalizedRect } from './model';
import type { CompositionLegendLayer } from './legend';
import { estimatedLegendHeightMm, fittedLegendBounds, wrapLegendText } from './legendLayout';

export const COMPOSITION_TEMPLATES = [
  { id: 'report', name: 'Report', description: 'Spacious map with a title and inset legend' },
  {
    id: 'analysis-landscape',
    name: 'Analysis Landscape',
    description: 'Wide main map with a narrow legend and square locator column',
  },
  {
    id: 'analysis-portrait',
    name: 'Analysis Portrait',
    description: 'Upper map with a lower locator and legend row',
  },
  { id: 'full-map', name: 'Full Map', description: 'Full page map with furniture overlays' },
] as const;
export type CompositionTemplateId = (typeof COMPOSITION_TEMPLATES)[number]['id'];

/** One-shot presentation preset. No identity, renderer mode, or subsequent layout enforcement. */
export function applyCompositionTemplate(
  composition: CompositionDraft,
  template: CompositionTemplateId,
  legendLayers: readonly CompositionLegendLayer[] = [],
  insetContent: InsetDefaultContent = {
    layers: legendLayers.map(({ layerId }) => ({ id: layerId })),
  },
): CompositionDraft {
  const page = compositionPageDimensionsMm(composition.page);
  // Match the existing area-based page scaling, bounded by the shorter edge on unusual ratios.
  const scalar = Math.min(
    Math.sqrt((page.width * page.height) / (297 * 210)),
    Math.min(page.width, page.height) / 210,
  );
  const marginX = 0.04;
  const marginY = 0.04;
  const full = template === 'full-map';
  const landscape = template === 'analysis-landscape';
  const portrait = template === 'analysis-portrait';
  const analysis = landscape || portrait;
  const typography = (existing: CompositionTypography, size: number, bold = false) => ({
    ...existing,
    fontSizePt: Math.max(4, size * scalar),
    fontWeight: bold ? ('bold' as const) : ('regular' as const),
    alignment: 'left' as const,
    colour: bold ? '#17212b' : '#52606d',
  });
  const gapX = (5 * scalar) / page.width;
  const gapY = (5 * scalar) / page.height;
  // Header uses physical type heights; an empty/hidden subtitle consumes no extra row.
  const subtitlePresent =
    (composition.subtitle.enabled || !composition.title.enabled) &&
    composition.subtitle.text.trim().length > 0;
  const titleSizeMm = 18 * scalar * (25.4 / 72);
  const titleLines = wrapLegendText(
    composition.title.text || 'Title',
    page.width * 0.9,
    (value) => [...value].length * titleSizeMm * 0.62,
  ).length;
  const titleHeight = titleSizeMm * 1.15 * titleLines;
  const subtitleY = marginY + (titleHeight + 2 * scalar) / page.height;
  const mapTop = subtitlePresent
    ? subtitleY + (10 * scalar * (25.4 / 72) * 1.15 + 3 * scalar) / page.height
    : marginY + (titleHeight + 4 * scalar) / page.height;
  const bottom = 0.94;
  const sidebarWidth = Math.min(0.245, ((bottom - mapTop) * 0.43 * page.height) / page.width);
  const sidebarX = 0.96 - sidebarWidth;
  const map: NormalizedRect = full
    ? { ...FILL_PAGE_BOUNDS }
    : {
        x: marginX,
        y: mapTop,
        width: landscape ? sidebarX - gapX - marginX : 0.92,
        height: bottom - mapTop,
      };
  const legendWidth = landscape
    ? sidebarWidth
    : portrait
      ? 0.575
      : Math.min(0.32, (57 * scalar) / page.width);
  const legend: NormalizedRect = {
    x: landscape
      ? sidebarX
      : portrait
        ? 0.96 - legendWidth
        : map.x + map.width - gapX - legendWidth,
    y: full ? 0.68 : map.y + (analysis ? 0 : gapY),
    width: legendWidth,
    height: analysis ? 0.29 : 0.25,
  };
  const inset = composition.insetMap ?? (analysis ? createDefaultInsetMap(insetContent) : null);
  const result: { -readonly [K in keyof CompositionDraft]: CompositionDraft[K] } = {
    ...composition,
    mapFrame: {
      ...composition.mapFrame,
      bounds: map,
      cornerRadiusMm: full ? 0 : 2 * scalar,
      border: { enabled: !full, widthMm: Math.max(0.1, 0.25 * scalar), colour: '#a2adb4' },
    },
    title: {
      ...composition.title,
      enabled: !full,
      position: { x: marginX, y: marginY },
      typography: typography(composition.title.typography, 18, true),
    },
    subtitle: {
      ...composition.subtitle,
      enabled: !full && subtitlePresent,
      position: { x: marginX, y: subtitleY },
      typography: typography(composition.subtitle.typography, 10),
    },
    legend: {
      ...composition.legend,
      columnCount: 1,
      enabled: true,
      // A generic fit anchor preserves upper placement when content later changes.
      autoFit: !analysis,
      autoFitAnchor: full ? 'bottom-right' : 'top-right',
      bounds: legend,
      background: { enabled: true, colour: '#ffffff', opacity: 90 },
      border: { enabled: analysis, widthMm: Math.max(0.1, 0.25 * scalar), colour: '#a2adb4' },
      cornerRadiusMm: 2 * scalar,
      paddingTopMm: 3 * scalar,
      paddingRightMm: 3 * scalar,
      paddingBottomMm: 3 * scalar,
      paddingLeftMm: 3 * scalar,
      groupSpacingMm: 2.4 * scalar,
      itemSpacingMm: 1.4 * scalar,
      bodyTypography: typography(composition.legend.bodyTypography, 8),
      heading: {
        ...composition.legend.heading,
        typography: typography(composition.legend.heading.typography, 10, true),
      },
    },
    scaleBar: {
      ...composition.scaleBar,
      enabled: true,
      style: 'simple-segmented',
      labelPosition: 'above',
      position: { x: map.x + gapX, y: map.y + map.height - gapY },
      maximumWidthMm: Math.max(10, Math.min(100, (analysis ? 40 : 45) * scalar)),
      fontSizePt: Math.max(4, 7 * scalar),
      thicknessMm: Math.max(0.1, Math.min(2, 0.35 * scalar)),
      colour: '#17212b',
      panel: {
        background: { enabled: true, colour: '#ffffff', opacity: 50 },
        border: { enabled: false, widthMm: Math.max(0.1, 0.25 * scalar), colour: '#a2adb4' },
        cornerRadiusMm: 1.5 * scalar,
        paddingMm: 2 * scalar,
      },
    },
    northArrow: {
      ...composition.northArrow,
      enabled: true,
      position: { x: map.x + gapX, y: map.y + gapY },
      style: 'circle-classic',
      sizeMm: Math.max(5, Math.min(50, 12 * scalar)),
    },
    sourcesAttribution: {
      ...composition.sourcesAttribution,
      width: full ? 0.54 : 0.92,
      position: { x: full ? 0.45 : marginX, y: full ? 0.965 : 0.955 },
      typography: {
        ...composition.sourcesAttribution.typography,
        fontSizePt: Math.max(5, Math.min(12, 6 * scalar)),
        alignment: 'right',
        colour: '#7c858d',
      },
    },
    insetMap: inset
      ? {
          ...inset,
          enabled: analysis,
          ...(analysis
            ? {
                bounds: { x: sidebarX, y: 0.5, width: 0.27, height: 0.22 },
                cornerRadiusMm: 2 * scalar,
                border: { enabled: true, widthMm: Math.max(0.1, 0.25 * scalar), colour: '#a2adb4' },
              }
            : {}),
        }
      : null,
  };
  if (analysis && result.insetMap) {
    const neededHeight =
      estimatedLegendHeightMm(result.legend, legendLayers, legendWidth * page.width) / page.height;
    if (landscape) {
      // A shorter custom page may need a slightly narrower square locator to retain legend rows.
      // Keep a readable supporting column; genuinely excessive content still receives overflow feedback.
      const fittedWidth = Math.min(
        sidebarWidth,
        Math.max(
          Math.min(sidebarWidth, 0.2),
          ((map.height - neededHeight - gapY) * page.height) / page.width,
        ),
      );
      const fittedX = 0.96 - fittedWidth;
      const insetHeight = (fittedWidth * page.width) / page.height;
      const fittedHeight =
        estimatedLegendHeightMm(result.legend, legendLayers, fittedWidth * page.width) /
        page.height;
      result.mapFrame = { ...result.mapFrame, bounds: { ...map, width: fittedX - gapX - marginX } };
      result.insetMap = {
        ...result.insetMap,
        bounds: { x: fittedX, y: bottom - insetHeight, width: fittedWidth, height: insetHeight },
      };
      result.legend = {
        ...result.legend,
        bounds: {
          ...legend,
          x: fittedX,
          width: fittedWidth,
          y: map.y,
          height: Math.min(Math.max(fittedHeight, 0.12), map.height - insetHeight - gapY),
        },
      };
    } else {
      const rowHeight = Math.min(Math.max(0.24, neededHeight), (bottom - mapTop) * 0.48);
      const rowY = bottom - rowHeight;
      result.mapFrame = { ...result.mapFrame, bounds: { ...map, height: rowY - gapY - mapTop } };
      result.insetMap = {
        ...result.insetMap,
        bounds: { x: marginX, y: rowY, width: legend.x - gapX - marginX, height: rowHeight },
      };
      result.legend = { ...result.legend, bounds: { ...legend, y: rowY, height: rowHeight } };
    }
  } else {
    result.legend = {
      ...result.legend,
      bounds: fittedLegendBounds(result.legend, legendLayers, map, page, {
        right: legend.x + legend.width,
        bottom: legend.y + legend.height,
        ...(!full ? { top: legend.y } : {}),
      }),
    };
  }
  // Anchor the OUTER scale panel, including labels/padding, equally far from both map edges.
  const scaleHeightMm =
    (result.scaleBar.panel?.paddingMm ?? 0) * 2 +
    Math.max(result.scaleBar.fontSizePt * (25.4 / 72) * 0.48, result.scaleBar.thicknessMm * 2) +
    result.scaleBar.fontSizePt * (25.4 / 72) * 1.43;
  result.scaleBar = {
    ...result.scaleBar,
    position: {
      x: result.mapFrame.bounds.x + gapX,
      y: Math.min(
        0.98,
        result.mapFrame.bounds.y +
          result.mapFrame.bounds.height -
          gapY -
          scaleHeightMm / page.height,
      ),
    },
  };
  return result;
}
